// FLAGS: health-data, personal-data
// The session-notes page for one consult, addressed by the URL's clientId and consultId: notes panel,
// session details, the client's other past sessions, and the edit-details dialog.
import { Box, Breadcrumbs } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { formatConsultDateExtendedWritten } from "@src/utils/formatter";
import { Link, To, useParams } from "react-router-dom";
import { useConsultQuery, useClientConsultsQuery } from "@src/queries/consults";
import {
  EditOutlined as EditOutlinedIcon,
  WarningAmberOutlined as WarningIcon,
} from "@mui/icons-material";
import { useConsultTagsQuery } from "@src/queries/tags";
import { TAG_TYPES } from "@src/constants";
import { format, isBefore, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import EmployerReferralToolkitBanner from "@src/views/employer/EmployerReferralToolkitBanner";
import { ResponseConsultsConsultWithShortNotesResponse } from "@src/api/v1/data-contracts";
import Button from "@openup/shared/components/Button/Button";
import SessionNotesPaper from "./SessionNotesPaper";
import SessionDetailsPaper from "./SessionDetailsPaper";
import PastSessionsTable from "../PastSessionsTable";
import EditSessionDetailsDialog from "./EditSessionDetailsDialog";

const SessionNotesView: FC = () => {
  const { clientId, consultId } = useParams();
  const { data: consult, refetch: refetchConsult } = useConsultQuery(
    clientId!,
    Number(consultId!),
  );

  const {
    data: consults,
    isLoading,
    refetch: refetchConsults,
  } = useClientConsultsQuery(clientId);
  const { consultTags: consultTopics } = useConsultTagsQuery(
    consult?.user?.id ?? "",
    consult?.id,
    TAG_TYPES.TOPICS,
  );

  const notesRef = useRef<HTMLElement>(null);
  const [pastSessions, setPastSessions] = useState<
    ResponseConsultsConsultWithShortNotesResponse[]
  >([]);
  const [isEditingDetails, setIsEditingDetails] = useState(false);

  useEffect(() => {
    if (!consults) return;
    const now = new Date();
    setPastSessions(
      consults.filter((c) => isBefore(parseISO(c.startedAt?.toString()), now)),
    );
  }, [consults]);

  const { t } = useTranslation();
  const refetchData = () => {
    refetchConsult();
    refetchConsults();
  };

  useEffect(() => {
    if (
      notesRef.current &&
      typeof notesRef.current.scrollIntoView === "function"
    ) {
      notesRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [consultId]);

  const breadcrumb = (text: string, url: To) => {
    return (
      <Link color="inherit" to={url}>
        {text}
      </Link>
    );
  };

  return (
    <>
      <Box className="ml-3" ref={notesRef}>
        {consult && (
          <Box>
            <Breadcrumbs separator="<">
              {breadcrumb("", "/clients")},{breadcrumb("Clients", "/clients")},
              {breadcrumb(
                `${consult.user.lastName}, ${consult.user.firstName}`,
                `/clients/${consult.user.id}`,
              )}
              ,
              {breadcrumb(
                `Session ${format(parseISO(consult.startedAt), "dd-MM-yy")}`,
                `/clients/${consult.user.id}/session-notes/${consult.id}`,
              )}
            </Breadcrumbs>
            <p className="mb-12 h1">
              Session notes - {formatConsultDateExtendedWritten(consult)}
            </p>
            {consult.convertedToMarkDown && (
              <div className="flex items-center p-4 mb-4 rounded-lg bg-yellow-100 text-yellow-700">
                <WarningIcon className="w-6 h-6 mr-3 fill-orange-500" />
                <div className="flex-grow whitespace-pre-line">
                  {t("SessionNotesView.NotesConverted")}
                </div>
              </div>
            )}
            <Box className="w-full mb-4">
              {consult?.employerOfClientHasReferralToolkit && (
                <EmployerReferralToolkitBanner
                  employerId={consult.user.employerId}
                />
              )}
            </Box>
            <Box className="flex flex-row w-full">
              <Box className="mr-3 mb-3 grow">
                <SessionNotesPaper
                  consult={consult}
                  consultIsLoading={isLoading}
                />
              </Box>
              <Box>
                <SessionDetailsPaper consult={consult} topics={consultTopics} />
                <Button
                  variant="secondary"
                  className="mt-2 mb-6 ml-0 px-6"
                  onClick={() => setIsEditingDetails(true)}
                >
                  <EditOutlinedIcon className="mr-1" />
                  Edit Details
                </Button>
              </Box>
            </Box>
            <Box className="w-full">
              <PastSessionsTable
                clientId={clientId!}
                consults={pastSessions}
                canAddSession={false}
                isLoading={isLoading}
              />
            </Box>
          </Box>
        )}
      </Box>
      <EditSessionDetailsDialog
        isOpen={isEditingDetails}
        clientId={consult?.user.id}
        consult={consult}
        onClose={() => setIsEditingDetails(false)}
        refetch={refetchData}
      />
    </>
  );
};

export default SessionNotesView;
